import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkExperience } from '../../hooks';
import { Button, Input, Card, CardHeader, CardBody, Alert, JobTitleField, FieldOfWorkField } from '../../components';

export const AddWorkExperience: React.FC = () => {
  const navigate = useNavigate();
  const { createWorkExperience, loading, error, clearError } = useWorkExperience();
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    job_title: '',
    employer: '',
    location: '',
    start_date: '',
    end_date: '',
    is_current: false,
    description: '',
    reason_for_leaving: '',
    field_of_work: '',
    company_website: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'is_current' && checked ? { end_date: '' } : {}),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);

    const data = { ...formData, end_date: formData.is_current ? undefined : formData.end_date || undefined };
    if (data.company_website && !/^https?:\/\//i.test(data.company_website)) {
      data.company_website = `https://${data.company_website}`;
    }
    const result = await createWorkExperience(data);
    if (result.success) {
      setSuccess(true);
      setTimeout(() => navigate('/profile'), 1500);
    }
  };

  return (
    <div>
      <Card collapsible={false}>
        <CardHeader>
          <h1 className="text-xl md:text-2xl font-bold">Add Work Experience</h1>
        </CardHeader>
        <CardBody>
          {error && (
            <Alert variant="danger" onClose={clearError} className="mb-4">
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" className="mb-4">
              Work experience added successfully!
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <JobTitleField
              value={formData.job_title}
              onChange={job_title => setFormData(prev => ({ ...prev, job_title }))}
              required
            />

            <Input
              label="Employer"
              name="employer"
              value={formData.employer}
              onChange={handleChange}
              placeholder="e.g., ABC Care Home"
              required
            />

            <Input
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., London, UK"
            />

            <Input
              label="Company Website"
              name="company_website"
              type="text"
              value={formData.company_website}
              onChange={handleChange}
              placeholder="e.g., acmecorp.com"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Start Date"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleChange}
                required
              />

              <Input
                label="End Date"
                name="end_date"
                type="date"
                value={formData.end_date}
                onChange={handleChange}
                disabled={formData.is_current}
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_current"
                name="is_current"
                checked={formData.is_current}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-brand-500 border-gray-300 rounded"
              />
              <label htmlFor="is_current" className="ml-2 block text-sm text-gray-700">
                I currently work here
              </label>
            </div>

            <FieldOfWorkField
              value={formData.field_of_work}
              onChange={field_of_work => setFormData(prev => ({ ...prev, field_of_work }))}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-h-[120px] text-base md:px-3 md:py-2 md:min-h-0"
                placeholder="Describe your responsibilities and achievements..."
              />
            </div>

            {!formData.is_current && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Leaving
                </label>
                <textarea
                  name="reason_for_leaving"
                  value={formData.reason_for_leaving}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-h-[96px] text-base md:px-3 md:py-2 md:min-h-0"
                  placeholder="e.g., Career progression, end of contract, relocation..."
                />
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Button type="submit" loading={loading} fullWidth className="sm:w-auto">
                Add Work Experience
              </Button>

              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/profile')}
                fullWidth
                className="sm:w-auto"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
};
